import { useState, useEffect } from 'react'
import type { MenuData, Config } from '@/types'
import { validateMenuData, validateConfig } from '@/lib/validation'
import { idb } from '@/lib/storage'

// ============================================================================
// Load JSON configuration files
// ============================================================================

async function loadConfig(): Promise<Config> {
  try {
    const response = await fetch('/config/config.json')
    if (!response.ok) {
      throw new Error('Failed to load config')
    }
    const data = await response.json()
    const validation = validateConfig(data)

    if (!validation.success) {
      console.error('Config validation failed:', validation.errors)
      throw new Error('Invalid config format')
    }

    return validation.data
  } catch (error) {
    console.error('Error loading config:', error)
    throw error
  }
}

async function loadMenuData(): Promise<MenuData> {
  try {
    // Try IndexedDB first (for admin changes in demo mode)
    const cachedMenu = await idb.get<MenuData>('menu-data')
    if (cachedMenu) {
      const validation = validateMenuData(cachedMenu)
      if (validation.success) {
        return validation.data
      }
    }

    // Fall back to JSON file
    const response = await fetch('/config/menu.json')
    if (!response.ok) {
      throw new Error('Failed to load menu data')
    }
    const data = await response.json()
    const validation = validateMenuData(data)

    if (!validation.success) {
      console.error('Menu data validation failed:', validation.errors)
      throw new Error('Invalid menu data format')
    }

    return validation.data
  } catch (error) {
    console.error('Error loading menu data:', error)
    throw error
  }
}

// ============================================================================
// Hook: useMenuData
// ============================================================================

export interface UseMenuDataReturn {
  config: Config | null
  menuData: MenuData | null
  isLoading: boolean
  error: string | null
  reload: () => Promise<void>
}

export function useMenuData(): UseMenuDataReturn {
  const [config, setConfig] = useState<Config | null>(null)
  const [menuData, setMenuData] = useState<MenuData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadData = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const [loadedConfig, loadedMenu] = await Promise.all([
        loadConfig(),
        loadMenuData(),
      ])

      setConfig(loadedConfig)
      setMenuData(loadedMenu)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load data'
      setError(message)
      console.error('Failed to load menu data:', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  return {
    config,
    menuData,
    isLoading,
    error,
    reload: loadData,
  }
}

// ============================================================================
// Hook: useConfig (config only)
// ============================================================================

export function useConfig(): Config | null {
  const [config, setConfig] = useState<Config | null>(null)

  useEffect(() => {
    loadConfig()
      .then(setConfig)
      .catch((err) => console.error('Failed to load config:', err))
  }, [])

  return config
}

// ============================================================================
// Hook: useMenu (menu data only)
// ============================================================================

export function useMenu(): MenuData | null {
  const [menu, setMenu] = useState<MenuData | null>(null)

  useEffect(() => {
    loadMenuData()
      .then(setMenu)
      .catch((err) => console.error('Failed to load menu:', err))
  }, [])

  return menu
}
